import React, { useState, useRef, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Box,
  Text,
  VStack,
  HStack,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  TagLabel,
  TagCloseButton,
  useOutsideClick,
} from "@chakra-ui/react";
import { Controller, useFormContext } from "react-hook-form";
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

// Width reserved for the search icon; also the input's left padding so the
// placeholder starts clear of it.
const SEARCH_ICON_SLOT = "32px";

const CommonMultiSelectField: React.FC<CommonMultiSelectFieldProps> = ({
  name,
  control,
  label,
  placeholder = i18next.t("courseCreate.selectOptions"),
  rules,
  options = [],
  maxHeight = "200px",
  maxSelectedHeight = "120px",
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("common");

  const {
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const errorMessage =
    typeof error === "object" && error !== null ? error.message : undefined;

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  // Close dropdown when clicking outside
  useOutsideClick({
    ref: dropdownRef,
    handler: closeDropdown,
  });

  const filteredOptions = options.filter((option) =>
    String(option.label).toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const getSelectedLabels = (selectedValues: (string | number)[]): string[] => {
    if (!selectedValues || selectedValues.length === 0) return [];
    return options
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label);
  };

  const toggleOption = (
    optionValue: string | number,
    currentValues: (string | number)[] = [],
    onChange: (values: (string | number)[]) => void
  ) => {
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter((val) => val !== optionValue)
      : [...currentValues, optionValue];
    onChange(newValues);
  };

  const removeOption = (
    optionValue: string | number,
    currentValues: (string | number)[] = [],
    onChange: (values: (string | number)[]) => void
  ) => {
    const newValues = currentValues.filter((val) => val !== optionValue);
    onChange(newValues);
  };

  const isEveryVisibleSelected = (currentValues: (string | number)[] = []) =>
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => currentValues.includes(option.value));

  const isSomeVisibleSelected = (currentValues: (string | number)[] = []) =>
    filteredOptions.some((option) => currentValues.includes(option.value)) &&
    !isEveryVisibleSelected(currentValues);

  // Select/deselect every option currently visible, so the action composes
  // with the search filter instead of discarding hidden selections.
  const toggleAllOptions = (
    currentValues: (string | number)[] = [],
    onChange: (values: (string | number)[]) => void
  ) => {
    const visibleValues = filteredOptions.map((option) => option.value);
    const newValues = isEveryVisibleSelected(currentValues)
      ? currentValues.filter((val) => !visibleValues.includes(val))
      : Array.from(new Set([...currentValues, ...visibleValues]));
    onChange(newValues);
  };

  return (
    <FormControl isInvalid={!!error}>
      <FormLabel>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        rules={rules}
        defaultValue={[]}
        render={({ field: { value = [], onChange } }) => (
          <Box position="relative" ref={dropdownRef}>
            {/* Trigger Button */}
            <Box
              as="button"
              type="button"
              onClick={() => (isOpen ? closeDropdown() : setIsOpen(true))}
              width="100%"
              minHeight="40px"
              bg="#FFF"
              color="#2F2F34"
              fontWeight={400}
              borderWidth="1px"
              borderColor={error ? "#E53E3E" : "#CDD6DC"}
              borderRadius="md"
              px={3}
              py={2}
              textAlign="left"
              cursor="pointer"
              _hover={{
                borderColor: error ? "#E53E3E" : "#1819B4",
              }}
              _focus={{
                borderColor: error ? "#E53E3E" : "#1819B4",
                boxShadow: "none",
                outline: "none",
              }}
              {...rest}
            >
              <Flex justify="space-between" align="flex-start" minHeight="24px">
                <Box
                  flex="1"
                  minWidth={0}
                  maxHeight={maxSelectedHeight}
                  overflowY="auto"
                >
                  {value.length === 0 ? (
                    <Text color="#A0AEC0" fontSize="14px">
                      {placeholder}
                    </Text>
                  ) : (
                    <Flex wrap="wrap" gap={1}>
                      {getSelectedLabels(value).map((label, index) => (
                        <Tag
                          key={index}
                          size="sm"
                          variant="solid"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          fontWeight={500}
                          fontSize={"14px"}
                          color={"#1819B4"}
                          p={"2px 6px"}
                          bgColor={"#0073E014"}
                        >
                          <TagLabel>{label}</TagLabel>
                          <TagCloseButton
                            onClick={(e) => {
                              e.stopPropagation();
                              const option = options.find(
                                (opt) => opt.label === label
                              );
                              if (option) {
                                removeOption(option.value, value, onChange);
                              }
                            }}
                          />
                        </Tag>
                      ))}
                    </Flex>
                  )}
                </Box>
                <Box ml={2} alignSelf="center">
                  {isOpen ? (
                    <ChevronUpIcon color="#798692" />
                  ) : (
                    <ChevronDownIcon color="#798692" />
                  )}
                </Box>
              </Flex>
            </Box>

            {/* Dropdown */}
            {isOpen && (
              <Box
                position="absolute"
                bottom="100%"
                left={0}
                right={0}
                zIndex={1000}
                bg="white"
                border="1px solid #CDD6DC"
                borderRadius="md"
                boxShadow="lg"
                mt={1}
                maxHeight={maxHeight}
                overflowY="auto"
              >
                {options.length === 0 ? (
                  <Box p={3}>
                    <Text color="#798692" fontSize="14px">
                      {t("courseCreate.noOptions")}
                    </Text>
                  </Box>
                ) : (
                  <VStack spacing={0} align="stretch">
                    {/* Search + select all stay pinned while the list scrolls */}
                    <Box
                      position="sticky"
                      top={0}
                      zIndex={1}
                      bg="white"
                      borderBottom="1px solid #E2E8F0"
                    >
                      <Box px={3} pt={2} pb={2}>
                        <InputGroup size="sm">
                          <InputLeftElement
                            pointerEvents="none"
                            width={SEARCH_ICON_SLOT}
                            height="100%"
                          >
                            <SearchIcon color="#798692" boxSize={3} />
                          </InputLeftElement>
                          <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder={t("courseCreate.searchOptions")}
                            fontSize="14px"
                            borderColor="#CDD6DC"
                            borderRadius="md"
                            // The Input theme sets `pl`, which would otherwise win over the
                            // padding InputGroup derives for the icon and let the placeholder
                            // run underneath it.
                            pl={SEARCH_ICON_SLOT}
                            _focus={{
                              borderColor: "#1819B4",
                              boxShadow: "none",
                            }}
                          />
                        </InputGroup>
                      </Box>

                      {filteredOptions.length > 0 && (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAllOptions(value, onChange);
                          }}
                          cursor="pointer"
                          _hover={{ bg: "#F7FAFC" }}
                          px={3}
                          py={2}
                          borderTop="1px solid #E2E8F0"
                        >
                          <HStack spacing={3}>
                            <Checkbox
                              isChecked={isEveryVisibleSelected(value)}
                              isIndeterminate={isSomeVisibleSelected(value)}
                              onChange={() => toggleAllOptions(value, onChange)}
                              colorScheme="blue"
                              size="sm"
                            />
                            <Text
                              fontSize="14px"
                              fontWeight={500}
                              color="#1819B4"
                            >
                              {isEveryVisibleSelected(value)
                                ? t("courseCreate.deselectAll")
                                : t("courseCreate.selectAll")}
                            </Text>
                          </HStack>
                        </Box>
                      )}
                    </Box>

                    {filteredOptions.length === 0 ? (
                      <Box p={3}>
                        <Text color="#798692" fontSize="14px">
                          {t("courseCreate.noSearchResults")}
                        </Text>
                      </Box>
                    ) : (
                      filteredOptions.map((option) => (
                        <Box
                          key={option.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOption(option.value, value, onChange);
                          }}
                          cursor="pointer"
                          _hover={{ bg: "#F7FAFC" }}
                          px={3}
                          py={2}
                          borderBottom="1px solid #E2E8F0"
                          _last={{ borderBottom: "none" }}
                        >
                          <HStack spacing={3}>
                            <Checkbox
                              isChecked={value.includes(option.value)}
                              onChange={() =>
                                toggleOption(option.value, value, onChange)
                              }
                              colorScheme="blue"
                              size="sm"
                            />
                            <Text fontSize="14px" color="#2F2F34">
                              {option.label}
                            </Text>
                          </HStack>
                        </Box>
                      ))
                    )}
                  </VStack>
                )}
              </Box>
            )}
          </Box>
        )}
      />
      <FormErrorMessage>
        {typeof errorMessage === "string" ? errorMessage : null}
      </FormErrorMessage>
    </FormControl>
  );
};

export default CommonMultiSelectField;
